import { useRef, useEffect, useCallback, useState } from 'react';
const globalPortalBaseUrl = process.env.REACT_APP_DEFAULT_ORGANIZATION_BASE_URL;

/**
 * Creates a path to the Bonfire opportunity from the given `projectId`
 * if it contains an environment prefix, e.g. 'CA.2831'.
 * @param {string} projectId The Project ID containing an environment prefix.
 * @returns {string} An empty string or a path to the Bonfire opportunity.
 */
export const getBonfireOpportunityPath = projectId => {
  const i = projectId.indexOf('.');
  return i === -1 ? '' : `/opportunities/${projectId.substring(i + 1)}`;
};

export const useIframeLogin = () => {
  const iframeRef = useRef();
  const timeoutRef = useRef(null);
  const projectsToOpen = useRef([]);
  const [renderIframe, setRenderIframe] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const jwt = localStorage.getItem('global-opportunities-jwt');

  const navigateRequestedWindows = useCallback(token => {
    // Can use same token for multiple opens, but they have a short lifespan
    projectsToOpen.current.forEach(({ path, baseUrl, windowRef }) => {
      let fullUrl = `${baseUrl}${path}`;
      if (token) {
        fullUrl = `${baseUrl}/login/auth/transfer?token=${token}&bounceUrl=${path}`;
      }
      windowRef.location.href = fullUrl;
    });

    projectsToOpen.current = [];
  }, []);

  const handleMessage = useCallback(
    event => {
      // event.data.status should be 'success' or 'failure'.
      if (event.origin !== globalPortalBaseUrl || event.data.status === undefined) {
        return;
      }
      const { token } = event.data;
      clearTimeout(timeoutRef.current);
      navigateRequestedWindows(token);
    },
    [navigateRequestedWindows]
  );
  // Clear on dismount
  useEffect(() => () => clearTimeout(timeoutRef.current));

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  const handleLoadGoToProjectIframe = useCallback(() => {
    const iframeElement = iframeRef.current;

    // Send the JWT to the iframe so the script running in the iframe can use it to authenticate and
    // generate a new Core App session.
    iframeElement.contentWindow.postMessage(jwt, globalPortalBaseUrl);
    // Fallback
    timeoutRef.current = setTimeout(() => {
      navigateRequestedWindows(null);
      setIframeError(true);
    }, 2000);
  }, [jwt, navigateRequestedWindows]);

  const logInToOrganizationDomain = useCallback(
    async (baseUrl, path) => {
      if (iframeError) {
        window.open(`${baseUrl}${path}`, '_blank');
        return;
      }
      const windowRef = window.open('', '_blank');
      projectsToOpen.current.push({ baseUrl, path, windowRef });
      if (!renderIframe) {
        setRenderIframe(true);
      } else {
        iframeRef.current.contentWindow.postMessage(jwt, globalPortalBaseUrl);
      }
    },
    [iframeError, jwt, renderIframe]
  );

  return {
    logInToOrganizationDomain,
    handleLoadGoToProjectIframe,
    iframeRef,
    iframeSrc:
      renderIframe &&
      !iframeError &&
      `${globalPortalBaseUrl}/login/authenticateWithGlobalOpportunitiesJwt`,
  };
};
